import { useState, useEffect } from "react";
import CryptoJS from "crypto-js";
import { mutate } from "swr";
import { useSearchParams } from "react-router-dom";
import { Box, Card, CardContent, Tab, Typography } from "@mui/material";
import EventsList from "./EventsList";
import AddOrEditEvent from "./AddOrEditEvent";
import localize from "../../../common/localize";
import ModalBackground from "../../../components/common/ModalBackground";
import useGlobalContext from "../../../hooks/useGlobalContext";
import EventsFilterAndAddButton from "./EventsFilterAndAddButton";
import ErrorMessage from "../../../components/common/ErrorMessage";
import {
  addFilterToLocalStorage,
  getClearEventModel,
  getEventsFilterOptions,
  getUserData,
  getUserPersonalId,
  getUserPersonalName,
  defaultFilteringForEvent,
} from "../../../common/EntitiesManagement";
import { updateEntity } from "../../../context/GlobalContext";
import ModalFilter from "../../../components/common/ModalFilter";
import AssignEvent from "./AssignEvent";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import CalendarView from "./CalendarView";
import EntityPrinter from "../../../components/common/EntityPrinter";
import { decryptForUrl, getFiltersInfo } from "../../../common/utils";

export default function EventsTabs({ myServicesMode = false }) {
  const {
    toggleVisible,
    eventModel,
    setEventModel,
    EVENTS_API,
    config,
    setCity,
    setCityFilterCode,
    setMunicipalityFilterCode,
    createEntity,
    showEditEventModal,
    setShowEditEventModal,
    showAssignEventModal,
    setShowAssignEventModal,
    filterForEvents,
    setFilterForEvents,
    setPageIndexEvents,
  } = useGlobalContext();
  const [selectedTab, setSelectedTab] = useState("0");
  const [searchParams] = useSearchParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [showModalAfterEventCreation, setShowModalAfterEventCreation] =
    useState(false);
  const [newEventId, setNewEventId] = useState(0);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    if (searchParams && searchParams.get("filter")) {
      try {
        const newFilters = JSON.parse(
          decryptForUrl(searchParams.get("filter")),
        );

        setFilterForEvents([...newFilters]);
        setSelectedFilter(null);
        addFilterToLocalStorage("events_filter", [...newFilters]);
      } catch (error) {
        console.error("Error loading search params", error);
      }
    }
  }, [searchParams]);

  // "Mis servicios" mode: on mount, scope the list to the logged-in user's
  // assigned personal, keeping the default date range. The PersonalAssigned
  // filter is applied by default and is not offered in the filter options.
  useEffect(() => {
    if (!myServicesMode) {
      return;
    }
    const personalId = getUserPersonalId();
    const defaultFilters = [...defaultFilteringForEvent(true)];
    if (personalId) {
      defaultFilters.push({
        Name: "PersonalAssigned",
        FriendlyName: localize.assigned_to,
        FilterValue: { Value: personalId },
        Value: getUserData()?.UserName ?? "",
      });
    }
    setFilterForEvents(defaultFilters);
  }, [myServicesMode]);

  const addFilter = (newFilters) => {
    const filters = Array.isArray(newFilters) ? newFilters : [newFilters];
    let current = [...(filterForEvents ?? [])];
    filters.forEach((f) => {
      const idx = current.findIndex((x) => x.Name === f.Name);
      if (idx >= 0) {
        current[idx] = f;
      } else {
        current.push(f);
      }
    });
    setFilterForEvents(current);
    setSelectedFilter(null);
    addFilterToLocalStorage("events_filter", current);
  };

  const applyFiltering = (filters) => {
    if (Array.isArray(filters)) {
      let current = [...(filterForEvents ?? [])];
      filters.forEach((f) => {
        const idx = current.findIndex((x) => x.Name === f.Name);
        if (idx >= 0) {
          current[idx] = f;
        } else {
          current.push(f);
        }
      });
      setFilterForEvents(current);
      addFilterToLocalStorage("events_filter", current);
    } else if (selectedFilter) {
      addFilter(selectedFilter);
    }
    setShowFilterModal(false);
    setSelectedFilter(null);
  };

  const validateOperationResult = (result) => {
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([EVENTS_API, config]);
    window.location.reload(false);
  };

  const handleAssignRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${EVENTS_API}/${data.Id}/assign`}`,
      { ...data, AssignedBy: getUserData()?.UserName },
      config,
    );
    validateOperationResult(updateResult, false);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${EVENTS_API}/${data.Id}`}`,
      data,
      config,
    );
    validateOperationResult(updateResult, false);
  };

  const validateCreationResult = (result) => {
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    if (result.data > 0) {
      setNewEventId(result.data);
      setShowModalAfterEventCreation(true);
      return;
    }

    mutate([EVENTS_API, config]);
    window.location.reload(false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(`${`${EVENTS_API}`}`, data, config);
    validateCreationResult(updateResult);
  };

  const handleTabChange = (e, newValue) => {
    setPageIndexEvents(newValue === "0" ? 0 : -1);
    setSelectedTab(newValue);
  };

  const disableChangesForEvent = () =>
    eventModel?.EventTypeId <= 0 ||
    eventModel?.StateId <= 0 ||
    eventModel?.CityId <= 0 ||
    !eventModel?.Address ||
    eventModel?.Address.replaceAll(" ", "").length <= 0 ||
    !eventModel?.ReporterPhoneNumber ||
    eventModel?.ReporterPhoneNumber.replaceAll(" ", "").length <= 0 ||
    !eventModel?.ReporterName ||
    eventModel?.ReporterName.replaceAll(" ", "").length <= 0;

  const disableChangesForAssignment = () =>
    !eventModel?.PersonalId || eventModel?.PersonalId <= 0;

  const showPrinter = () => {
    setShowPrinterModal(true);
  };

  const getColumnTitles = () => {
    return [
      localize.code,
      localize.event_type,
      localize.operational_status,
      localize.execution_status,
      localize.proceeding,
      localize.assigned_to,
      localize.total_value,
      localize.payed,
      localize.billed,
      localize.expected_attention_date,
      localize.contractor,
      localize.client_name,
      localize.phone_number,
      localize.address,
      localize.department,
      localize.city,
      localize.zone,
      localize.modified_by,
      localize.modification_date,
    ];
  };

  return (
    <div className="lateral_padding">
      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          title={localize.print}
          report_title={localize.events}
          report_subtitle={getFiltersInfo(filterForEvents)}
          url={EVENTS_API}
          filters={filterForEvents ?? []}
          columns={getColumnTitles()}
          objectTypeName={"event"}
        />
      )}

      {showModalAfterEventCreation && (
        <ModalBackground
          showModal={showModalAfterEventCreation}
          setShowModal={setShowModalAfterEventCreation}
          title={localize.new_event}
          onClickToSubmit={() => {
            window.open(`/events/detail/${newEventId}`, "_blank");
            setShowModalAfterEventCreation(false);
            mutate([EVENTS_API, config]);
            window.location.reload(false);
          }}
          onClickToCancel={() => {
            setShowModalAfterEventCreation(false);
            mutate([EVENTS_API, config]);
            window.location.reload(false);
          }}
          okButtonText={localize.open}
          cancelButtonText={localize.ignore}
          Component={<Typography>{localize.new_visit_generated}</Typography>}
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showAssignEventModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={setShowAssignEventModal}
          setShowModal={setShowAssignEventModal}
          title={`${localize.assign_event} (${localize.code}: ${
            eventModel?.Code ?? `${localize.app_name}-${localize.Id}`
          })`}
          onClickToSubmit={async () => {
            await handleAssignRequest(eventModel);
            setShowAssignEventModal(false);
            setEventModel(getClearEventModel());
          }}
          onClickToCancel={() => {
            setShowAssignEventModal(false);
            setEventModel(getClearEventModel());
          }}
          Component={<AssignEvent />}
          validators={disableChangesForAssignment()}
        />
      )}

      {showEditEventModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditEventModal}
          setShowModal={setShowEditEventModal}
          title={`${localize.edit_event} #${eventModel.Id}`}
          onClickToSubmit={async () => {
            await handleUpdateRequest(eventModel);
            setShowEditEventModal(false);
            setCity(null);
            setCityFilterCode(null);
            setMunicipalityFilterCode(null);
            setEventModel(getClearEventModel());
          }}
          onClickToCancel={() => {
            setShowEditEventModal(false);
            setCity(null);
            setCityFilterCode(null);
            setMunicipalityFilterCode(null);
            setEventModel(getClearEventModel());
          }}
          Component={<AddOrEditEvent />}
          validators={disableChangesForEvent()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={myServicesMode ? localize.my_services : localize.events}
            subtitle={
              myServicesMode
                ? `${localize.my_services_subtitle} ${getUserPersonalName() ?? ""}.`
                : localize.events_description
            }
          />

          <EventsFilterAndAddButton
            printButtonMethod={showPrinter}
            hideAddButton={myServicesMode}
            hiddenFilterNames={myServicesMode ? ["PersonalAssigned"] : []}
            filterForEvents={filterForEvents}
            setFilterForEvents={setFilterForEvents}
            handleAddBtn={() => {
              setEventModel(getClearEventModel());
              setShowAddModal(true);
            }}
            toggleVisible={toggleVisible}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_event}
                onClickToSubmit={async () => {
                  await handleCreateRequest(eventModel);
                  setShowAddModal(false);
                  setCity(null);
                  setCityFilterCode(null);
                  setMunicipalityFilterCode(null);
                  setEventModel(getClearEventModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setCity(null);
                  setCityFilterCode(null);
                  setMunicipalityFilterCode(null);
                  setEventModel(getClearEventModel());
                }}
                Component={<AddOrEditEvent />}
                validators={disableChangesForEvent()}
              />
            }
            handleFilterBtn={() => {
              setCity(null);
              setShowFilterModal(true);
            }}
            filterModal={
              <ModalBackground
                isDrawerMode
                showModal={showFilterModal}
                setShowModal={setShowFilterModal}
                title={localize.filter_list}
                okButtonText={localize.accept}
                onClickToCancel={() => setShowFilterModal(false)}
                onClickToSubmit={() => {
                  applyFiltering();
                }}
                Component={
                  <ModalFilter
                    filterOptions={getEventsFilterOptions().filter(
                      (option) =>
                        !(myServicesMode && option.Name === "PersonalAssigned"),
                    )}
                    currentFilters={filterForEvents}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    onSubmit={applyFiltering}
                  />
                }
              />
            }
          />
        </CardContent>
      </Card>

      <div className="lateral_padding">
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleTabChange}>
              <Tab label={localize.list} value="0" />
              <Tab label={localize.calendar} value="1" />
            </TabList>
          </Box>
          <TabPanel value="0" index={0} sx={{ padding: "10px 0px 0px 0px" }}>
            <EventsList hideEditButton={myServicesMode} />
          </TabPanel>
          <TabPanel value="1" index={1} sx={{ padding: "10px 0px 0px 0px" }}>
            <CalendarView />
          </TabPanel>
        </TabContext>
      </div>
    </div>
  );
}
